import AvatarImage from '../components/AvatarImage';
import { useState, useContext, useEffect } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Eye, EyeOff, Lock, Shield, Settings, ChevronRight, Home, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import AddressAutocompleteV2 from '../components/AddressAutocompleteV2';
import { normalizeApiError } from '../utils/normalizeError';
import { SKILL_LEVELS, normalizeSkillLevel } from '../constants/skillLevels';
import { logProfileView, logAddressAutocompleteUsed, logEngagementEvent } from '../utils/engagementTracker';
import Footer from '../components/Footer';
import MobileProfileMenu from '../components/MobileProfileMenu';
import DateOfBirthPicker from '../components/DateOfBirthPicker';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';
import { useAvatarOperation } from '../hooks/useAvatarOperation';

// Helper: Calculate age from date of birth (YYYY-MM-DD)
const calculateAgeFromDob = (dob) => {
  if (!dob) return null;
  try {
    const [year, month, day] = dob.split('-').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch {
    return null;
  }
};

const Profile = () => {
  const { user: authUser, token, beginUserUpdate } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [initialData, setInitialData] = useState(null);
  const [form, setForm] = useState(null);
  const [stats, setStats] = useState({ 
    followers: 0, 
    following: 0,
    runs_played: 0,
    show_rate: null,
    last_active_at: null,
    follower_previews: []
  });
  const [isSaving, setIsSaving] = useState(false);
  const avatarOperation = useAvatarOperation(authUser?.id, apiClient);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [addressInput, setAddressInput] = useState('');
  
  // View mode: 'card' (Player Card) vs 'edit' (Settings)
  const [viewMode, setViewMode] = useState(() => new URLSearchParams(location.search).get('edit') === 'privacy' ? 'edit' : 'card');

  useEffect(() => {
    if (viewMode !== 'edit' || new URLSearchParams(location.search).get('edit') !== 'privacy') return;
    window.requestAnimationFrame(() => document.getElementById('profile-privacy-settings')?.scrollIntoView({ block: 'center' }));
  }, [location.search, viewMode]);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Unsaved-changes protection (only meaningful in the edit view).
  const isProfileDirty = Boolean(
    (initialData && form && JSON.stringify(form) !== JSON.stringify(initialData)) ||
    currentPassword || newPassword || confirmPassword
  );
  const profileGuardActive = viewMode === 'edit' && isProfileDirty;
  const { promptOpen, confirmLeave, cancelLeave, guardedLeave } = useUnsavedChangesGuard({
    active: profileGuardActive,
    shouldPrompt: profileGuardActive,
  });

  // Helper: seed form from server user object
  const seedFrom = (userObj) => ({
    date_of_birth: userObj.date_of_birth ?? '',
    age_group: userObj.age_group ?? 'High School',
    // Home Address fields
    address_line1: userObj.address_line1 ?? '',
    address_line2: userObj.address_line2 ?? '',
    address_city: userObj.address_city ?? '',
    address_state: userObj.address_state ?? '',
    address_postal_code: userObj.address_postal_code ?? '',
    address_country: userObj.address_country ?? 'US',
    // Progressive name collection (Phase 1 — voluntary in Profile edit).
    first_name: userObj.first_name ?? '',
    last_name: userObj.last_name ?? '',
    // Profile fields
    profile_bio: userObj.profile_bio ?? '',
    // Phone (SMS opt-in is managed ONLY via Notification Settings page)
    phone_number: userObj.phone_number ?? '',
    // Skill
    self_rated_skill: normalizeSkillLevel(userObj.self_rated_skill)
  });

  // Seed form state exactly once per user identity
  useEffect(() => {
    if (authUser?.id && !initialData) {
      const seed = seedFrom(authUser);
      setInitialData(seed);
      setForm(seed);
      // Initialize address input with existing address
      if (authUser.address_line1) {
        setAddressInput(authUser.address_line1);
      }
    }
  }, [authUser?.id]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await apiClient.get(`/follow/stats?user_id=${authUser?.id}`);
        setStats({
          followers: response.data.followers || 0,
          following: response.data.following || 0,
          runs_played: response.data.runs_played || 0,
          show_rate: response.data.show_rate,
          last_active_at: response.data.last_active_at,
          follower_previews: response.data.follower_previews || []
        });
      } catch (error) {
        console.error('Failed to load follow stats', error?.response?.data || error);
      }
    };
    if (authUser?.id) {
      loadStats();
    }
  }, [authUser?.id]);

  // Format last active timestamp
  const formatLastActive = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      
      // Handle invalid dates or future dates
      if (isNaN(diffMs) || diffMs < 0) return null;
      
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      
      if (diffDays === 0) {
        if (diffHours === 0) return 'Active now';
        if (diffHours === 1) return 'Active 1 hour ago';
        return `Active ${diffHours} hours ago`;
      } else if (diffDays === 1) {
        return 'Played 1 day ago';
      } else if (diffDays < 7) {
        return `Played ${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return weeks === 1 ? 'Played 1 week ago' : `Played ${weeks} weeks ago`;
      } else {
        const months = Math.floor(diffDays / 30);
        return months === 1 ? 'Played 1 month ago' : `Played ${months} months ago`;
      }
    } catch {
      return null;
    }
  };

  // Track profile view on mount
  useEffect(() => {
    if (authUser?.id) {
      logProfileView({
        has_bio: !!authUser.profile_bio,
        has_avatar: !!authUser.profile_image_url,
        has_address: !!authUser.address_line1
      });
    }
  }, [authUser?.id]);

  // Functional state update to preserve all fields
  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Build minimal PATCH payload — only changed, non-blank fields.
  // Excludes `first_name` / `last_name` — those are persisted via the
  // dedicated `PATCH /profile/name` endpoint (with stricter validation).
  // Including them here would be a silent no-op (UserUpdate doesn't accept
  // them) AND risk a future regression if someone adds them to UserUpdate.
  const buildPayload = () => {
    if (!form || !initialData) return null;
    const p = {};
    for (const [k, v] of Object.entries(form)) {
      if (k === 'first_name' || k === 'last_name') continue;
      const changed = initialData[k] !== v;
      const blank = v === '' || v == null;
      
      // Boolean fields should always be included if changed (even if false)
      if (k === 'is_private_profile') {
        if (changed) {
          p[k] = v;
        }
      } else if (changed && !blank) {
        // Date of birth is sent as-is (YYYY-MM-DD string)
        p[k] = v;
      }
    }
    return Object.keys(p).length > 0 ? p : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple simultaneous saves
    if (isSaving) return;
    
    // Validate required fields
    if (!form?.address_line1 || form.address_line1.trim() === '') {
      toast.error('Address Line 1 is required');
      return;
    }
    
    const update = beginUserUpdate();
    try {
      const payload = buildPayload();
      // Determine if name fields changed (handled by dedicated endpoint).
      const firstChanged = (initialData?.first_name ?? '') !== (form?.first_name ?? '');
      const lastChanged = (initialData?.last_name ?? '') !== (form?.last_name ?? '');
      const namesChanged = firstChanged || lastChanged;
      const firstTrim = (form?.first_name ?? '').trim();
      const lastTrim = (form?.last_name ?? '').trim();
      // /profile/name requires both fields present together. Only fire when
      // user has filled BOTH; if they cleared one, skip the call (existing
      // names stay on the record).
      const shouldPatchName = namesChanged && firstTrim && lastTrim;

      if ((!payload || Object.keys(payload).length === 0) && !shouldPatchName) {
        toast.info('No changes to save');
        return;
      }

      // Set saving state
      setIsSaving(true);

      // PATCH with minimal payload — main profile fields (no names)
      if (payload && Object.keys(payload).length > 0) {
        await apiClient.patch(`/users/profile`, payload);
        if (!update.isCurrent()) return;
      }
      // PATCH name via dedicated endpoint with light validation. Use the
      // response *directly* to update local form + initialData so the inputs
      // and live preview reflect the saved value immediately — without waiting
      // on the /auth/me rehydrate (which is async and was sometimes leaving
      // the inputs stuck on the prior value).
      let nameResponse = null;
      if (shouldPatchName) {
        const nameRes = await apiClient.patch('/profile/name', {
          first_name: firstTrim,
          last_name: lastTrim,
        });
        if (!update.isCurrent()) return;
        nameResponse = nameRes?.data || null;
      }

      // Log engagement event for profile update
      const fieldsUpdated = [
        ...(payload ? Object.keys(payload) : []),
        ...(shouldPatchName ? ['first_name', 'last_name'] : []),
      ];
      await logEngagementEvent('profile_update', {
        fields_updated: fieldsUpdated,
        update_count: fieldsUpdated.length
      });

      if (!update.isCurrent()) return;
      // Rehydrate from server - authoritative source
      const meRes = await apiClient.get('/auth/me');
      if (!update.isCurrent()) return;
      const userFresh = meRes.data;

      // If we just saved names, the PATCH /profile/name response is the
      // most authoritative source for those two fields. Layer it on top of
      // the /auth/me payload so a slow replica or middleware can never leave
      // the inputs stuck on the previous value.
      if (nameResponse) {
        if (nameResponse.first_name !== undefined) userFresh.first_name = nameResponse.first_name;
        if (nameResponse.last_name !== undefined) userFresh.last_name = nameResponse.last_name;
        if (nameResponse.display_name !== undefined) userFresh.display_name = nameResponse.display_name;
      }

      if (!update.apply(userFresh)) return;
      // Update form state with fresh server data
      const freshSeed = seedFrom(userFresh);
      setInitialData(freshSeed);
      setForm(freshSeed);

      
      toast.success('Profile updated successfully!');
    } catch (error) {
      if (!update.isCurrent()) return;
      toast.error(normalizeApiError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) {
      toast.error('No photo received from camera. Try again.');
      return;
    }

    // Validate file type (allow blank mime — iOS Camera sometimes provides no type)
    if (file.type && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Avatar uploads are persisted immediately; this is only an input limit.
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Photo must be 10 MB or smaller.');
      return;
    }

    if (avatarOperation.pending) return;
    const update = beginUserUpdate();
    const formData = new FormData();
    formData.append('file', file);
    await avatarOperation.run('upload', {
      request: config => apiClient.post('/profile/upload-avatar', formData, {
        ...config, headers: { 'Content-Type': 'multipart/form-data' }
      }),
      onMutationSuccess: data => {
        if (!update.isCurrent()) return;
        setAvatarPreview(data.profile_image_url);
        // Backend confirmation is sufficient even if the bounded refresh fails.
        // Avatar fields are outside the editable form baseline: preserve text edits.
        update.apply(current => ({ ...current,
          profile_image_url: data.profile_image_url,
          profile_image_version: data.profile_image_version,
          profile_image_content_type: 'image/jpeg',
        }));
      },
      onRefreshSuccess: userFresh => {
        if (!update.apply(userFresh)) return;
        toast.success('Avatar uploaded successfully!');
      },
    });
  };

  const handleDeleteAvatar = async () => {
    if (!authUser?.profile_image_url) return;

    if (avatarOperation.pending) return;
    const update = beginUserUpdate();
    await avatarOperation.run('remove', {
      request: config => apiClient.delete('/profile/avatar', config),
      onMutationSuccess: () => { if (update.isCurrent()) setAvatarPreview(null); },
      onRefreshSuccess: userFresh => {
        if (!update.apply(userFresh)) return;
        toast.success('Avatar deleted successfully!');
      },
    });
  };

  const handleAddressSelect = (address, formattedAddress) => {
    // Update address fields from Google Maps autocomplete
    setForm(prev => ({
      ...prev,
      address_line1: address.formatted_address || formattedAddress,
      address_city: address.city || '',
      address_state: address.state || '',
      address_postal_code: address.zip_code || ''
    }));
    setAddressInput(formattedAddress || address.formatted_address);
    
    // Track engagement: address autocomplete used
    logAddressAutocompleteUsed({
      city: address.city || '',
      state: address.state || '',
      zip_code: address.zip_code || '',
      country: address.country || 'US',
      lat: address.lat,
      lng: address.lng
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 12) {
      toast.error('New password must be at least 12 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    try {
      setIsChangingPassword(true);
      
      await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      // Clear form and close section
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
      
      toast.success('Password changed successfully!');
      
      // Log engagement event
      await logEngagementEvent('password_change', {});
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(normalizeApiError(error));
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Defense-in-depth: never render the protected self-profile (or its
  // "Player"/"U"/zero-stats fallback) without a validated user. The route guard
  // (requireUser in App.js) is the primary gate; this guarantees it even if the
  // component is reached another way. Placed after all hooks (Rules of Hooks).
  if (!authUser) {
    return <Navigate to="/" replace />;
  }


  // ============================================================
  // RENDER: PLAYER CARD VIEW
  // ============================================================
  const renderPlayerCard = () => {
    return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <MobileProfileMenu />
      <div className="mx-auto max-w-lg px-4 py-6">
        {/* Header with Back + Home + Settings */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mobile-page-back h-10 px-0 text-sm font-medium text-zinc-500 hover:text-zinc-900 hover:bg-transparent"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="mobile-page-back h-8 w-8"
              data-testid="nav-home-btn"
              aria-label="Dashboard"
            >
              <Home className="w-4 h-4 text-zinc-400" />
            </Button>
          </div>
          <Button
            variant="ghost"
            onClick={() => setViewMode('edit')}
            className="h-10 px-3 text-sm font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg"
            data-testid="edit-profile-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>

        {/* ====== PLAYER CARD ====== */}
        <div className="bg-white rounded-2xl shadow-sm border border-zinc-100 overflow-hidden">
          
          {/* Top Section: Avatar + Identity */}
          <div className="px-6 pt-8 pb-2 text-center">
            {/* Large Avatar */}
            <div className="relative inline-block mb-4">
              <AvatarImage
                src={avatarPreview || authUser?.profile_image_url}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover ring-4 ring-zinc-100"
                data-testid="player-avatar"
              >
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-zinc-300 to-zinc-400 flex items-center justify-center ring-4 ring-zinc-100">
                  <span className="text-3xl font-bold text-white">
                    {authUser?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </AvatarImage>
            </div>

            {/* Username - Bold, prominent */}
            <h1 className="text-2xl font-extrabold text-zinc-900 tracking-tight mb-0.5" data-testid="player-username">
              {authUser?.username || 'Player'}
            </h1>

            {/* Location */}
            <p className="text-sm text-zinc-500" data-testid="player-location">
              {authUser?.address_city && authUser?.address_state 
                ? `${authUser.address_city}, ${authUser.address_state}`
                : 'Location not set'}
            </p>

            {/* Bio (if exists) - increased line height */}
            {authUser?.profile_bio && (
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-xs mx-auto" data-testid="player-bio">
                {authUser.profile_bio}
              </p>
            )}

            {/* Certified badges - only if present */}
            {(authUser?.certified_skill_level || authUser?.is_certified_elite) && (
              <div className="mt-3 flex items-center justify-center gap-2">
                {authUser?.certified_skill_level && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    ✓ {authUser.certified_skill_level}
                  </span>
                )}
                {authUser?.is_certified_elite && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                    ★ Elite
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ====== STATS GRID - Improved Clarity ====== */}
          <div className="flex justify-between items-start px-6 py-6 border-b border-zinc-100" data-testid="stats-grid">
            {/* Followers - Social Proof */}
            <button
              onClick={() => navigate('/profile/followers')}
              className="flex-1 flex flex-col items-center hover:opacity-70 transition-opacity"
              data-testid="stat-followers"
            >
              <p className="text-3xl font-bold text-zinc-900 leading-none">{stats.followers}</p>
              <p className="text-xs font-semibold text-zinc-500 mt-1 uppercase tracking-wide">Followers</p>
            </button>

            {/* Runs Played - Activity */}
            <div 
              className="flex-1 flex flex-col items-center border-x border-zinc-100 px-2"
              data-testid="stat-runs"
            >
              <p className="text-3xl font-bold text-zinc-900 leading-none">{stats.runs_played}</p>
              <p className="text-xs font-semibold text-zinc-500 mt-1 uppercase tracking-wide">Runs</p>
            </div>

            {/* Show Rate - Reliability Badge */}
            <div 
              className="flex-1 flex flex-col items-center"
              data-testid="stat-showrate"
            >
              {stats.show_rate !== null ? (
                <>
                  <p className="text-3xl font-bold leading-none text-orange-500">
                    {stats.show_rate}%
                  </p>
                  <p className="text-xs font-semibold text-zinc-500 mt-1 uppercase tracking-wide">Show Rate</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-zinc-300 leading-none">—</p>
                  <p className="text-xs font-semibold text-zinc-400 mt-1 uppercase tracking-wide">Show Rate</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">Building history</p>
                </>
              )}
            </div>
          </div>

          {/* ====== REPUTATION BADGE (if earned) ====== */}
          {stats.show_rate !== null && stats.show_rate >= 80 && (
            <div className="px-6 py-3 bg-green-50 border-b border-green-100">
              <div className="flex items-center justify-center gap-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Reliable Player
                </span>
                <span className="text-xs text-green-600">• {stats.show_rate}% show rate</span>
              </div>
            </div>
          )}

          {/* ====== SOCIAL PROOF (DIRECTLY UNDER STATS) ====== */}
          <div className="px-6 pb-6 space-y-3">
            {/* Follower Avatars + Text */}
            {stats.followers > 0 ? (
              <button
                onClick={() => navigate('/profile/followers')}
                className="flex items-center justify-center gap-2 w-full group"
                data-testid="follower-previews"
              >
                {stats.follower_previews.length > 0 && (
                  <div className="flex -space-x-1.5">
                    {stats.follower_previews.slice(0, 3).map((follower, idx) => (
                      <div
                        key={follower.id || idx}
                        className="w-6 h-6 rounded-full ring-2 ring-white overflow-hidden"
                        style={{ zIndex: 3 - idx }}
                      >
                        <AvatarImage
                          src={follower.profile_image_url}
                          alt={follower.username}
                          className="w-full h-full object-cover"
                        >
                          <div className="w-full h-full bg-zinc-200 flex items-center justify-center">
                            <span className="text-[8px] font-semibold text-zinc-500">
                              {follower.username?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        </AvatarImage>
                      </div>
                    ))}
                  </div>
                )}
                <span className="text-xs text-zinc-500 group-hover:text-zinc-600 transition-colors">
                  {stats.followers === 1 
                    ? 'Followed by 1 player'
                    : `Followed by ${stats.followers} players`}
                </span>
              </button>
            ) : (
              <p className="text-xs text-zinc-500 text-center">No followers yet</p>
            )}

            {/* Activity (minimal) */}
            {formatLastActive(stats.last_active_at) && (
              <div className="flex items-center justify-center gap-1.5" data-testid="last-active">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400"></div>
                <span className="text-xs text-zinc-500">
                  {formatLastActive(stats.last_active_at)}
                </span>
              </div>
            )}
          </div>

          {/* ====== FOLLOWING / ACTIONS ====== */}
          <div className="border-t border-zinc-50 px-6 py-4">
            <button
              onClick={() => navigate('/profile/following')}
              className="flex items-center w-full text-sm text-zinc-500 hover:text-zinc-700 transition-colors group"
              data-testid="following-link"
            >
              <span>Following <span className="font-semibold text-zinc-700">{stats.following}</span> {stats.following === 1 ? 'player' : 'players'}</span>
              <ChevronRight className="w-4 h-4 text-zinc-300 ml-auto group-hover:text-zinc-500" />
            </button>
          </div>
        </div>

        {/* Privacy & Safety Link - Outside card, subtle */}
        <button
          onClick={() => navigate('/profile/blocked')}
          className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-600 mt-6 transition-colors mx-auto"
          data-testid="blocked-users-link"
        >
          <Shield className="w-3.5 h-3.5" />
          Blocked Users
        </button>
      </div>
      <Footer variant="slim" />
    </div>
  )};

  // ============================================================
  // RENDER: EDIT PROFILE VIEW (Settings)
  // ============================================================
  const renderEditProfile = () => (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-lg px-4 py-6">
        {/* Back to Player Card */}
        <Button
          variant="ghost"
          onClick={() => guardedLeave(() => setViewMode('card'))}
          className="mb-6 h-10 px-0 text-sm font-medium text-zinc-500 hover:text-zinc-900 hover:bg-transparent"
          data-testid="back-to-card-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Button>

        <h1 className="text-2xl font-bold text-zinc-900 mb-6">Edit Profile</h1>

        {/* Avatar Upload Section */}
        <div className="mb-8">
          <Label className="text-sm text-zinc-600 mb-3 block">Profile Photo</Label>
          <div className="flex items-center gap-4">
            <AvatarImage
              src={avatarPreview || authUser?.profile_image_url}
              alt="Profile"
              className="w-16 h-16 rounded-full object-cover ring-2 ring-zinc-100"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 flex items-center justify-center ring-2 ring-zinc-100">
                <span className="text-xl font-bold text-zinc-500">
                  {authUser?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            </AvatarImage>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={avatarOperation.pending}
                onClick={() => document.getElementById('avatar-upload').click()}
                className="text-sm"
              >
                {avatarOperation.pending ? ({ uploading: 'Uploading photo…', removing: 'Removing photo…', refreshing: 'Refreshing profile…' }[avatarOperation.phase]) : (authUser?.profile_image_url ? 'Change' : 'Upload')}
              </Button>
              <input
                id="avatar-upload"
                data-testid="avatar-upload-input"
                type="file"
                accept="image/png,image/jpeg,image/heic,image/heif,image/webp"
                onChange={handleAvatarUpload}
                className="sr-only"
              />
              {authUser?.profile_image_url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={avatarOperation.pending}
                  onClick={handleDeleteAvatar}
                  className="text-sm text-zinc-500 hover:text-red-600"
                >
                  Remove
                </Button>
              )}
              {avatarOperation.pending && (
                <Button type="button" variant="ghost" size="sm" onClick={avatarOperation.cancel}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>

        {avatarOperation.phase !== 'idle' && (
          <p role="status" aria-live="polite" data-testid="avatar-operation-status" className="text-sm text-zinc-500 mt-2">
            {avatarOperation.pending
              ? ({ uploading: 'Uploading photo', removing: 'Removing photo', refreshing: 'Refreshing profile' }[avatarOperation.phase])
              : `${avatarOperation.phase === 'completed' ? 'Completed' : 'Failed'}: ${avatarOperation.message}`}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name — Phase 1 voluntary collection. Public display will use
              "First L." (last initial only). Username remains your @handle. */}
          <div data-testid="profile-name-section">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name" className="text-sm text-zinc-600">First name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  data-testid="first-name-input"
                  placeholder="e.g. Alex"
                  maxLength={50}
                  value={form?.first_name ?? ''}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-sm text-zinc-600">Last name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  data-testid="last-name-input"
                  placeholder="e.g. Johnson"
                  maxLength={50}
                  value={form?.last_name ?? ''}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-1.5">
              Hosts and players will see <span className="font-medium text-zinc-500">{
                (form?.first_name?.trim() && form?.last_name?.trim())
                  ? `${form.first_name.trim()} ${form.last_name.trim()[0].toUpperCase()}.`
                  : (form?.first_name?.trim() || `@${authUser?.username || 'you'}`)
              }</span>
            </p>
          </div>

          {/* Bio */}
          <div>
            <Label htmlFor="profile_bio" className="text-sm text-zinc-600">Bio</Label>
            <Input
              id="profile_bio"
              name="profile_bio"
              data-testid="bio-input"
              placeholder="e.g., Weekend warrior, pass-first PG"
              value={form?.profile_bio ?? ''}
              onChange={(e) => handleChange('profile_bio', e.target.value)}
              maxLength={150}
              className="mt-1"
            />
            <span className="text-xs text-zinc-400 mt-1 block">
              {(form?.profile_bio?.length || 0)} / 150
            </span>
          </div>

          {/* Date of Birth & Age */}
          <div className="grid grid-cols-2 gap-4">
            {authUser?.date_of_birth ? (
              <div>
                <Label className="text-sm text-zinc-600">Date of Birth</Label>
                <div className="mt-1.5 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-md text-zinc-700">
                  {new Date(authUser.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <p className="mt-1 text-xs text-zinc-500">Cannot be changed</p>
              </div>
            ) : (
              <DateOfBirthPicker
                value={form?.date_of_birth ?? ''}
                onChange={(val) => handleChange('date_of_birth', val)}
                required
                showLabel={true}
                labelClassName="text-sm text-zinc-600"
              />
            )}
            <div>
              <Label htmlFor="age" className="text-sm text-zinc-600">Age</Label>
              <Input
                id="age"
                name="age"
                type="text"
                data-testid="age-display"
                value={(() => {
                  // Always compute age from date_of_birth (single source of truth)
                  const computedAge = calculateAgeFromDob(authUser?.date_of_birth);
                  return computedAge !== null ? `${computedAge} years` : 'Not set';
                })()}
                disabled
                className="mt-1 bg-zinc-50 text-zinc-500"
              />
            </div>
          </div>
          
          {/* Player Group */}
          <div>
            <Label htmlFor="age_group" className="text-sm text-zinc-600">Player Group</Label>
            <Select
              value={form?.age_group ?? 'High School'}
              onValueChange={(value) => handleChange('age_group', value)}
            >
              <SelectTrigger data-testid="age-group-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Middle School">Middle School</SelectItem>
                <SelectItem value="High School">High School</SelectItem>
                <SelectItem value="College">College</SelectItem>
                <SelectItem value="23-34">23-34</SelectItem>
                <SelectItem value="35-49">35-49</SelectItem>
                <SelectItem value="50-75">50-75</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Skill Level */}
          <div>
            <Label htmlFor="self_rated_skill" className="text-sm text-zinc-600">Skill Level</Label>
            <Select
              value={form?.self_rated_skill ?? 'Unranked'}
              onValueChange={(value) => handleChange('self_rated_skill', value)}
            >
              <SelectTrigger data-testid="skill-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SKILL_LEVELS.map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-zinc-500 mt-1">
              Self-assessment. Scouts can certify your actual level.
            </p>
          </div>

          {/* Mobile Phone */}
          <div>
            <Label htmlFor="phone_number" className="text-sm text-zinc-600">Mobile Phone</Label>
            <Input
              id="phone_number"
              name="phone_number"
              type="tel"
              data-testid="phone-input"
              placeholder="Enter your phone number"
              value={form?.phone_number ?? ''}
              onChange={(e) => handleChange('phone_number', e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-zinc-500 mt-2">
              Manage SMS preferences in{' '}
              <span 
                onClick={() => navigate('/notification-settings')}
                className="text-orange-600 hover:underline cursor-pointer"
              >
                SMS Notifications
              </span>
            </p>
          </div>

          {/* Address Section */}
          <div className="border-t border-zinc-100 pt-5">
            <p className="text-sm font-semibold text-zinc-900 mb-3">Home Address</p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="address_line1" className="text-sm text-zinc-600">Address</Label>
                <AddressAutocompleteV2
                  value={addressInput || form?.address_line1 || ''}
                  onChange={(value) => {
                    setAddressInput(value);
                    handleChange('address_line1', value);
                  }}
                  onSelect={handleAddressSelect}
                  placeholder="Start typing your address..."
                />
              </div>

              <div>
                <Label htmlFor="address_line2" className="text-sm text-zinc-600">Apt / Suite</Label>
                <Input
                  id="address_line2"
                  name="address_line2"
                  data-testid="address2-input"
                  placeholder="Optional"
                  value={form?.address_line2 ?? ''}
                  onChange={(e) => handleChange('address_line2', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="address_city" className="text-sm text-zinc-600">City</Label>
                  <Input
                    id="address_city"
                    name="address_city"
                    data-testid="address-city-input"
                    value={form?.address_city ?? ''}
                    onChange={(e) => handleChange('address_city', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="address_state" className="text-sm text-zinc-600">State</Label>
                  <Input
                    id="address_state"
                    name="address_state"
                    data-testid="address-state-input"
                    placeholder="NY"
                    value={form?.address_state ?? ''}
                    onChange={(e) => handleChange('address_state', e.target.value)}
                    required
                    maxLength={2}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="address_postal_code" className="text-sm text-zinc-600">Zip</Label>
                  <Input
                    id="address_postal_code"
                    name="address_postal_code"
                    data-testid="address-postal-input"
                    value={form?.address_postal_code ?? ''}
                    onChange={(e) => handleChange('address_postal_code', e.target.value)}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Section */}
          <div id="profile-privacy-settings" className="border-t border-zinc-100 pt-5">
            <p className="text-sm font-semibold text-zinc-900 mb-3">Privacy</p>
            
            <label className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg cursor-pointer">
              <div className="flex-1">
                <p className="text-sm font-medium text-zinc-900">Private Profile</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Followers need your approval. Followers/following lists are hidden from non-followers.
                </p>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={form?.is_private_profile || false}
                  onChange={(e) => handleChange('is_private_profile', e.target.checked)}
                  className="sr-only peer"
                  data-testid="private-profile-toggle"
                />
                <div className="w-11 h-6 bg-zinc-300 rounded-full peer peer-checked:bg-orange-500 transition-colors"></div>
                <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-5"></div>
              </div>
            </label>
          </div>

          {/* Save Button */}
          <Button 
            type="submit" 
            className="w-full h-12 rounded-xl font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-transform duration-75 active:scale-[0.98]" 
            data-testid="save-btn"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>

        {/* Password Change Section */}
        <div className="border-t border-zinc-100 pt-6 mt-6">
          <button
            type="button"
            onClick={() => setShowPasswordSection(!showPasswordSection)}
            className="flex items-center gap-2 text-sm font-semibold text-zinc-900 hover:text-zinc-700 transition-colors"
            data-testid="change-password-toggle"
          >
            <Lock className="w-4 h-4" />
            Change Password
            <span className="text-zinc-400 text-xs ml-1">
              {showPasswordSection ? '(hide)' : '(show)'}
            </span>
          </button>

          {showPasswordSection && (
            <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
              <div>
                <Label htmlFor="current_password" className="text-sm text-zinc-600">
                  Current Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new_password" className="text-sm text-zinc-600">
                  New Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="new_password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 12 characters)"
                    data-testid="new-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-zinc-400 mt-1">
                  {newPassword.length}/12 characters minimum
                </p>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-sm text-zinc-600">
                  Confirm New Password
                </Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                  className="mt-1"
                />
                {confirmPassword && newPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
                {confirmPassword && newPassword && confirmPassword === newPassword && (
                  <p className="text-xs text-green-600 mt-1">Passwords match</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-[44px] rounded-xl font-semibold bg-zinc-800 text-white hover:bg-zinc-700 transition-transform duration-75 active:scale-[0.98]"
                data-testid="change-password-btn"
                disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              >
                {isChangingPassword ? 'Changing Password...' : 'Update Password'}
              </Button>
            </form>
          )}
        </div>

        {/* Account Settings Section */}
        <div className="border-t border-zinc-100 pt-5">
          <p className="text-sm font-semibold text-zinc-900 mb-3">Account Management</p>
          <p className="text-xs text-zinc-500 mb-3">
            Manage your email, password, and other sensitive account settings
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/account-settings')}
            className="w-full gap-2 justify-start"
          >
            <Shield className="w-4 h-4" />
            <span>Account Settings</span>
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>
        </div>
      </div>
      <Footer variant="slim" />
      <DiscardChangesDialog
        open={promptOpen}
        onKeepEditing={cancelLeave}
        onDiscard={confirmLeave}
      />
    </div>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return viewMode === 'card' ? renderPlayerCard() : renderEditProfile();
};

export default Profile;
